import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthentication } from "../Authentication/context";
import { getFirstRequestRoute, getTimesheetFilters, resolveRequestType, resolveTimesheetFilter } from "../Request/permissions";

const getMyRequestType = (type) => {
	switch (type) {
		case "cancel":
		case "absence":
			return "pto";
		case "shift":
		case "edit-shift":
			return "clock";
		case "schedule":
		case "edit-schedule":
		case "submit-schedule":
		case "swap-shifts":
		case "offer-shifts":
			return "schedule";
		case "availability":
			return "availability";
		default:
			return ""
	}
};

export const useNotificationRedirect = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { accessMap } = useAuthentication();

	const navigateToRequest = useCallback((namespace, desiredType, data) => {
		const query = new URLSearchParams(searchParams);
		if (data?.resourceId) {
			query.set("id", data.resourceId);
		}
		if (data?.requestType) {
			query.set("type", data.requestType);
		}

		const resolvedRequestType = resolveRequestType(accessMap, namespace, desiredType);
		if (!resolvedRequestType) {
			navigate(getFirstRequestRoute(accessMap));
			return;
		}

		query.set("requestType", resolvedRequestType);

		if (namespace === "timesheet") {
			const filters = getTimesheetFilters(accessMap, resolvedRequestType);
			const targetFilter = resolveTimesheetFilter(accessMap, resolvedRequestType, query.get("type") || filters[0]);
			if (!targetFilter) {
				navigate(getFirstRequestRoute(accessMap));
				return;
			}
			query.set("type", targetFilter);
		}

		const urlPath = namespace === "faceRecognition" ? "face-recognition" : namespace;
		navigate(`/request/${urlPath}?${query.toString()}`);
	}, [accessMap, navigate, searchParams]);

	return useCallback(
		(type, data) => {
			switch (type) {
				case "people:job":
				case "people:compensation":
				case "people:organization":
				case "people:status":
				case "people:hire":
				case "people:personal:information": {
					// accessMap && getAccessValue(accessMap, "people.canView")
					navigate("/people");
					break;
				}
				case "employee:clockin":
				case "employee:clockout":
				case "employee:start-break":
				case "employee:end-break": {
					// hasAllAccessInList(accessMap, ["clockin.canView", "canManageUsers"])
					navigate("/time-clock/my");
					break;
				}
				case "employee:request:shift":
				case "employee:request:edit-shift": {
					navigateToRequest("clock", "team", data);
					break;
				}

				case "employee:request:absence":
				case "employee:request:cancel": {
					navigateToRequest("pto", "team", data);
					break;
				}

				case "employee:request:face-enrollment":
				case "employee:request:face-mismatch": {
					navigateToRequest("faceRecognition", "team", data);
					break;
				}

				case "employee:request:offer-shifts":
				case "employee:request:swap-shifts":
				case "employee:request:edit-schedule":
				case "employee:request:submit-schedule":
				case "employee:request:schedule": {
					// hasAllAccessInList(accessMap, ["request.canView", "canManageUsers"])
					navigateToRequest("schedule", "team", data);
					break;
				}
				case "employee:request:reject":
				case "employee:request:approve": {
					// hasAllAccessInList(accessMap, ["timeSheet.canView", "canManageUsers"])
					const reqType = getMyRequestType(data.requestType);
					navigateToRequest(reqType, "my", data);
					break;
				}
				case "schedule:update":
				case "schedule:delete":
				case "schedule:approved":
				case "schedule:locked":
				case "schedule:unlocked":
				case "schedule:unapproved":
				case "schedule:submitted":
				case "schedule:reject":
					navigate(`/scheduler/my?mode=date&from=${data?.resourceDate}&to=${data?.resourceDate}`);
				break;
				case "license:expired":
					navigate("/profile/qualifications/licenses");
					break;
				case "certification:expired":
					navigate("/profile/qualifications/certifications");
					break;
				default: 
					console.log("No redirect for notification type: ", type);
					break;
			}
		},
		[navigate, navigateToRequest]
	);
};
